/**
 * API Module Exports
 * Central export for all API functions and types
 */

// ============================================================================
// CLIENT
// ============================================================================
export { apiClient, ApiClient } from "./client";

// ============================================================================
// AUTH
// ============================================================================
export {
  login,
  logout,
  getCurrentUser,
  refreshToken,
  isAuthenticated,
  verify2FA,
  resend2FACode,
  enable2FA,
  disable2FA,
  get2FAStatus,
  acceptToF,
  acceptLegalDocuments,
} from "./auth";
export type { LoginResult } from "./auth";

// ============================================================================
// OVERVIEW (Aggregated data for multiple pages)
// ============================================================================
export { getOverview, getOverviewQuickStats } from "./overview";

// ============================================================================
// VPP (Virtual Power Plant)
// ============================================================================
export {
  getRealtimeGauges,
  getCurrentPrices,
  getTrailing24h,
  getVPPMetrics,
  getVPPPageData,
} from "./vpp";

// ============================================================================
// CMS (Condition Monitoring System)
// ============================================================================
export {
  getCMSOverview,
  getAlerts,
  getErrors,
  getNotifications,
  getTemperatureMonitoring,
  getZeroYield,
  getInverterCountdown,
  getSystemStatus,
  updateAlert,
  getLatestWarning,
} from "./cms";

// ============================================================================
// PRICES
// ============================================================================
export {
  getPriceHistory,
  getPriceMatrix,
  getLucrativeHours,
  getPriceStatistics,
  generatePriceForecast,
  getPriceForecastMatrix,
  getForecastedLucrativeHours,
  getPriceForecastStatistics,
  evaluateForecastModel,
  getConsecutiveExpensiveHours,
} from "./prices";

// ============================================================================
// ARCHIVE
// ============================================================================
export {
  getProductionArchive,
  getArchiveSummary,
  getMonthlyArchive,
  getYearlyArchive,
  downloadArchive,
  downloadArchiveCSV,
  getChartData,
  getInverterData,
  getRangeChartData,
} from "./archive";

// ============================================================================
// MAINTENANCE
// ============================================================================
export {
  getMaintenanceMatrix,
  getMaintenanceWindows,
  getMaintenanceRecommendations,
  getMaintenanceForecastMatrix,
  getMaintenanceForecastWindows,
  getMaintenanceForecastRecommendations,
  filterWorkingHours,
  formatMaintenanceWindow,
} from "./maintenance";

// ============================================================================
// FORECAST (Volume Forecast & EVI)
// ============================================================================
export {
  getVolumeForecast,
  getForecastSummary,
  downloadForecastCSV,
  triggerForecastDownload,
  getEVIForecast,
  getEVIColor,
  getEVILabel,
} from "./forecast";

// ============================================================================
// PLANTS
// ============================================================================
export { getPlants, getPlant } from "./plants";

// ============================================================================
// SUPPORT
// ============================================================================
export {
  getSupportContact,
  getSupportFAQs,
  getSupportEmails,
} from "./support";

// ============================================================================
// PREFERENCES
// ============================================================================
export {
  getProfessionalView,
  setProfessionalView,
  getUserPreferences,
  updateUserPreferences,
  getGranularity,
} from "./preferences";

// ============================================================================
// PROFILE
// ============================================================================
export { getUserProfile } from "./profile";

// ============================================================================
// ADMIN (Company & User Management)
// ============================================================================
export {
  // Companies
  getCompanies,
  getCompany,
  createCompany,
  updateCompany,
  deleteCompany,
  // Users
  getUsers,
  getUser,
  createUser,
  updateUser,
  resetUserPassword,
  deactivateUser,
} from "./admin";

// ============================================================================
// PPD (Revenue Calculator)
// ============================================================================
export {
  getPlantTypes,
  calculatePPD,
  exportPDF,
  formatCurrency,
  formatPercent,
  formatEnergy,
  PLANT_TYPE_LABELS,
  DEGRADATION_RATES,
} from "./ppd";

// ============================================================================
// EV CHARGING
// ============================================================================
export {
  getChargingStations,
  getChargingSessions,
  getChargingDashboard,
  getHourlyPower,
  getYearlySummary,
  getChargingDataRange,
  getDailyEnergy,
} from "./charging";

// ============================================================================
// PERMISSIONS
// ============================================================================
export {
  getMyPermissions,
  // Company-level
  getCompanyModules,
  setCompanyModules,
  getCompanyPlants,
  setCompanyPlants,
  getCompanyChargingStations,
  setCompanyChargingStations,
  getCompanyEVIProducers,
  setCompanyEVIProducers,
  getCompanyEVIConsumers,
  setCompanyEVIConsumers,
  // User-level
  getUserModules,
  setUserModules,
  getUserPlants,
  setUserPlants,
  getUserChargingStations,
  setUserChargingStations,
  getUserEVIProducers,
  setUserEVIProducers,
  getUserEVIConsumers,
  setUserEVIConsumers,
} from "./permissions";

// ============================================================================
// TYPES
// ============================================================================
export type {
  // Auth
  LoginRequest,
  LoginResponse,
  LoginResponseWith2FA,
  TwoFAVerifyRequest,
  TwoFAEnableResponse,
  TwoFADisableResponse,
  TwoFAResendRequest,
  TwoFAResendResponse,
  TwoFAStatusResponse,
  User,
  // Company & License
  Company,
  CompanyCreate,
  CompanyUpdate,
  CompanyListResponse,
  // User Profile
  UserProfileInfo,
  // Admin User Management
  AdminUserCreate,
  AdminUserUpdate,
  PasswordResetResponse,
  UserListResponse,
  // Overview
  OverviewQuickStats,
  OverviewResponse,
  ProductionOverview,
  PlantStatus,
  CurrentPrices,
  AlertsSummary,
  AlertItem,
  FinancialSummary,
  // VPP
  RealtimeGauge,
  Trailing24hData,
  TrailingDataPoint,
  Trailing24hProduction,
  Trailing24hPrices,
  VPPMetrics,
  CurrentPricesResponse,
  // CMS
  CMSOverview,
  AlertsTierResponse,
  ErrorsTierResponse,
  NotificationsTierResponse,
  ErrorItem,
  NotificationItem,
  TemperatureMonitoringResponse,
  TemperatureReading,
  ZeroYieldResponse,
  ZeroYieldDevice,
  InverterCountdownResponse,
  InverterLifecycle,
  SystemStatus,
  SystemStatusComponent,
  AlertUpdate,
  AlertResponse,
  // Prices
  EnergyPriceResponse,
  PriceMatrix,
  PriceMatrixRow,
  LucrativeHour,
  PriceStatistics,
  PriceForecastMatrix,
  PriceForecastMatrixRow,
  ForecastedLucrativeHour,
  PriceForecastStatistics,
  ForecastGenerateResponse,
  ForecastEvaluateResponse,
  // Maintenance
  MaintenanceWindow,
  ForecastedMaintenanceWindow,
  // Archive
  ProductionArchiveResponse,
  ProductionRecord,
  ArchiveSummary,
  MonthlyArchiveItem,
  YearlyArchiveItem,
  DailyArchiveItem,
  // Production Chart
  ChartDataPoint,
  ChartSeries,
  ChartSummary,
  ProductionChartResponse,
  InverterChartData,
  InverterChartResponse,
  RangeDataPoint,
  RangeChartResponse,
  // Forecast
  VolumeForecastResponse,
  VolumeForecastItem,
  VolumeForecastSummary,
  EVIProfile,
  EVIProductiveHour,
  // Support
  SupportContactResponse,
  ContactInfo,
  SupportFAQResponse,
  FAQItem,
  SupportEmailsResponse,
  // Preferences
  ProfessionalViewResponse,
  ProfessionalViewUpdate,
  DataGranularity,
  UserPreferencesResponse,
  UserPreferencesUpdate,
  // Plants
  Plant,
  PlantCreate,
  PlantProduction,
  // EV Charging
  ChargingStation,
  ChargingSession,
  ChargingMeterValue,
  ChargingDashboard,
  HourlyPowerEntry,
  YearlySummaryEntry,
  ChargingDataRangeResponse,
  DailyEnergyEntry,
  // Permissions
  ModuleKey,
  EffectivePermissions,
  CompanyModulesResponse,
  UserModulesResponse,
  CompanyPlantsResponse,
  UserPlantsResponse,
  CompanyChargingStationsResponse,
  UserChargingStationsResponse,
  CompanyEVIProducersResponse,
  UserEVIProducersResponse,
  CompanyEVIConsumersResponse,
  UserEVIConsumersResponse,
  // Error
  APIError,
} from "./types";

// PPD Types (from ppd.ts)
export type {
  PlantType,
  CostItem,
  PlantTypeInfo,
  PlantTypesResponse,
  PPDInput,
  RevenueTableRow,
  AmortizationTableRow,
  SummaryMetrics,
  ChartData,
  PPDInputEcho,
  PPDResult,
  BacktestTableRow,
  BacktestSummary,
} from "./ppd";
